'use client';
import { useEffect, useState } from 'react';
import { BaseTranslations } from '../../../baseTranslations';
import { CmsProduct } from '../../../types/cmsProduct';
import { VerticalType } from '../../../types/cmsTypes';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import {
  currencyFormat,
  formatNumberWithSeparator,
} from '../../../utils/helpers';
import {
  calculateOffersValue,
  calculateRewardValue,
} from '../../../utils/helpers/calculateRewardValue';
import {
  getApplyThrough,
  getTitleWelcomeOffer,
  getWelcomeGiftInfo,
  hasOfferGroupLeadIn,
} from '../../../utils/helpers/offerGroupLabels';
import {
  filterActiveOffers,
  getChooseOneOffers,
  mapOfferToPeriodGifts,
} from '../../../utils/helpers/offers';
import { getClientTranslations } from '../../../utils/translations/getClientTranslations';
import { tr } from '../../../utils/translations/tr';
import { MaterialIcon } from '../../MaterialIcon';
import { TRANSLATIONS as OFFERS_TRANSLATIONS } from '../../ProductCard/i18n';
import { RichText } from '../../RichText/index';
import { GrayInfoBlock } from '../BaseComponents/GrayInfoBlock';
import { ProductDetailsGiftGroup } from '../BaseComponents/ProductDetailsGiftGroup';
import { ProductDetailsSectionHeader } from '../BaseComponents/ProductDetailsSectionHeader';
import { ProductDetailsSectionWrapper } from '../BaseComponents/ProductDetailsSectionWrapper';

type ProductDetailsSectionGiftProps = CmsProduct & {
  vertical?: VerticalType;
  pushDataLayer?: PushDataLayerFn;
  /**
   * Section title override (also the analytics section title). The configured STK / BKA Offers
   * section (CMS-1935) passes the editor's `tab_label`; the default and TI builders keep the
   * translated `pd_nav_offers` default. Not `title`: the props also carry the spread product,
   * whose own `title` (its uid, the analytics item_id) must neither become the header nor be
   * dropped from the product.
   */
  sectionTitle?: string;
  /** Skip the own `<h2>` header when the parent section already renders one. */
  hideHeader?: boolean;
};

export const ProductDetailsSectionGift: React.FC<
  ProductDetailsSectionGiftProps
> = ({
  vertical,
  pushDataLayer,
  sectionTitle,
  hideHeader = false,
  ...product
}) => {
  const { welcome_offers, exclusive_offers, bundle_exclusive_offers } = product;
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);
  const offersSectionTitle = sectionTitle ?? tr('pd_nav_offers', staticText);

  const [now, setNow] = useState<Date | null>(null);
  useEffect(() => setNow(new Date()), []);

  const keepActive = (offers?: CmsProduct['exclusive_offers']) =>
    now ? filterActiveOffers(offers ?? [], now) : (offers ?? []);

  const activeExclusive = keepActive(exclusive_offers);
  const activeWelcome = keepActive(welcome_offers);
  const activeBundle = keepActive(bundle_exclusive_offers);

  const offers = [...activeExclusive, ...activeWelcome, ...activeBundle];
  // Welcome offers stay out of the choose-one carousel and its "choose from N"
  // count and get a group of their own, as on the product card (CMS-2032).
  const chooseOneGifts =
    mapOfferToPeriodGifts(
      getChooseOneOffers({
        exclusive_offers: activeExclusive,
        bundle_exclusive_offers: activeBundle,
      }),
    ) ?? [];
  const welcomeGifts = mapOfferToPeriodGifts(activeWelcome) ?? [];

  const totalRewardValue =
    calculateRewardValue({
      welcomeOffers: activeWelcome,
      exclusiveOffers: activeExclusive,
    }) + calculateOffersValue(activeBundle);

  const rewardPrefix = [...chooseOneGifts, ...welcomeGifts].find(
    (g) => g.gift_value?.prefix,
  )?.gift_value?.prefix;

  const offersText = getClientTranslations(OFFERS_TRANSLATIONS, context);
  const offerVertical = vertical ?? 'CREDIT_CARD';
  const showLeadIn = hasOfferGroupLeadIn(offerVertical);
  const giftsInfo = tr(getApplyThrough(offerVertical), offersText, {
    count: chooseOneGifts.length.toString(),
  });

  const rewardUpTo = formatNumberWithSeparator({
    value: totalRewardValue,
    thousandsSeparator: ',',
  });

  const rewardTitle = [
    offersText.brand_reward,
    rewardUpTo !== '0' &&
      `${tr('up_to', offersText)} ${currencyFormat(rewardPrefix)}${rewardUpTo}`,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      data-component="product-details-section-gift"
      className="flex flex-col gap-6"
    >
      {!hideHeader && (
        <ProductDetailsSectionHeader
          icon={<MaterialIcon icon="diamond" size={3} />}
        >
          {offersSectionTitle}
        </ProductDetailsSectionHeader>
      )}
      {chooseOneGifts.length > 0 && (
        <ProductDetailsGiftGroup
          group="choose-one"
          title={rewardTitle}
          info={showLeadIn ? giftsInfo : undefined}
          gifts={chooseOneGifts}
        />
      )}
      {welcomeGifts.length > 0 && (
        <ProductDetailsGiftGroup
          group="welcome"
          title={tr(getTitleWelcomeOffer(offerVertical), offersText)}
          info={
            showLeadIn
              ? tr(getWelcomeGiftInfo(offerVertical), offersText)
              : undefined
          }
          gifts={welcomeGifts}
        />
      )}
      {offers.length > 0 && (
        <ProductDetailsSectionWrapper>
          <div className="px-6 pt-6 pb-4 flex flex-col gap-6">
            {offers.map((offer, index) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={offer.uid ?? index}
                className="flex flex-col gap-4"
              >
                {offer.offer_title && (
                  <div className="mhg-alpha-typography-table-cell-primary">
                    {offer.offer_title}
                  </div>
                )}
                {offer?.introduction && (
                  <div className="mhg-alpha-typography-body-2-relaxed text-text/secondary">
                    <RichText>{offer.introduction}</RichText>
                  </div>
                )}
                <ul className="mhg-alpha-typography-body-2-relaxed text-text/secondary list-disc ml-6">
                  {offer.list_of_gifts?.map((gift) => (
                    <li key={gift?.gift?.uid}>{gift?.gift?.gift_title}</li>
                  ))}
                </ul>

                {offer.description && (
                  <GrayInfoBlock
                    title={tr('terms_and_conditions', staticText)}
                    product={product}
                    pushDataLayer={pushDataLayer}
                    sectionTitle={offersSectionTitle}
                    trackingTitle={offer.offer_title}
                  >
                    <RichText>{offer.description}</RichText>
                  </GrayInfoBlock>
                )}
              </div>
            ))}
          </div>
        </ProductDetailsSectionWrapper>
      )}
    </div>
  );
};
